import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ScatterChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const RentVsSqftChart = ({ data }) => {
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const units = [];
    data.forEach(building => {
      if (building.units && Array.isArray(building.units)) {
        building.units.forEach(unit => {
          const sqft = unit.squareFeet;
          const rent = unit.rent || 0;
          if (sqft != null && !isNaN(sqft) && sqft > 0 && rent > 0 && !isNaN(rent)) {
            units.push({ sqft: Number(sqft), rent: Number(rent) });
          }
        });
      }
    });

    if (units.length === 0) return [];

    const sqftValues = units.map(u => u.sqft).sort((a, b) => a - b);
    const minSqft = sqftValues[0];
    const maxSqft = sqftValues[sqftValues.length - 1];
    const range = maxSqft - minSqft;

    if (range === 0) {
      const allRents = units.map(u => u.rent).sort((a, b) => a - b);
      const median =
        allRents.length % 2 === 0
          ? (allRents[allRents.length / 2 - 1] + allRents[allRents.length / 2]) / 2
          : allRents[Math.floor(allRents.length / 2)];

      return [
        {
          sqftMidpoint: Math.round(minSqft),
          sqftMin: Math.round(minSqft),
          sqftMax: Math.round(maxSqft),
          label: `${Math.round(minSqft)} sq ft`,
          median: Math.round(median),
          count: units.length,
        },
      ];
    }

    const numBins = Math.min(Math.max(8, Math.floor(units.length / 10)), 12);
    const binWidth = range / numBins;

    const bins = {};
    for (let i = 0; i < numBins; i++) {
      const binMin = minSqft + i * binWidth;
      const binMax = i === numBins - 1 ? maxSqft : minSqft + (i + 1) * binWidth;
      bins[i] = {
        min: binMin,
        max: binMax,
        rents: [],
      };
    }

    units.forEach(unit => {
      const binIndex = Math.min(
        Math.floor((unit.sqft - minSqft) / binWidth),
        numBins - 1,
      );
      bins[binIndex].rents.push(unit.rent);
    });

    const result = [];
    Object.keys(bins)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach(binIndex => {
        const bin = bins[binIndex];
        if (bin.rents.length > 0) {
          const sorted = [...bin.rents].sort((a, b) => a - b);
          const median =
            sorted.length % 2 === 0
              ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
              : sorted[Math.floor(sorted.length / 2)];

          const midpoint = (bin.min + bin.max) / 2;
          const label =
            bin.max === maxSqft
              ? `${Math.round(bin.min)}-${Math.round(bin.max)}+`
              : `${Math.round(bin.min)}-${Math.round(bin.max)}`;

          result.push({
            sqftMidpoint: Math.round(midpoint),
            sqftMin: Math.round(bin.min),
            sqftMax: Math.round(bin.max),
            label,
            median: Math.round(median),
            count: bin.rents.length,
          });
        }
      });

    return result;
  }, [data]);

  if (!data || data.length === 0 || processedData.length === 0) {
    return (
      <Box
        sx={{
          height: '256px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F3F4F6',
          borderRadius: '0.5rem',
          border: '2px dashed #D1D5DB',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <TrendingUp
            size={48}
            color='#9CA3AF'
            style={{ marginBottom: '1rem' }}
          />
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              marginBottom: '0.25rem',
            }}
          >
            No data available
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
            Search for buildings to see scatter analysis
          </Typography>
        </Box>
      </Box>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            padding: '0.75rem',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            boxShadow: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            {data.label} sq ft
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            Median Rent: ${data.median.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
            {data.count} unit{data.count !== 1 ? 's' : ''}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ height: '300px', width: '100%' }}>
      <ResponsiveContainer width='100%' height='100%'>
        <ScatterChart
          data={processedData}
          margin={{
            top: 20,
            right: 20,
            left: 10,
            bottom: 40,
          }}
        >
          <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
          <XAxis
            type='number'
            dataKey='sqftMidpoint'
            name='Square Feet'
            label={{
              value: 'Square Footage',
              position: 'insideBottom',
              offset: -5,
              style: { fontSize: 12, fill: '#6B7280' },
            }}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickFormatter={(value) => {
              const dataPoint = processedData.find(d => d.sqftMidpoint === value);
              return dataPoint ? dataPoint.label : value;
            }}
          />
          <YAxis
            type='number'
            dataKey='median'
            name='Rent'
            label={{
              value: 'Median Rent ($)',
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 12, fill: '#6B7280' },
            }}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickFormatter={(value) => `$${value.toLocaleString()}`}
          />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
          <Scatter
            data={processedData}
            dataKey='median'
            fill='#39585C'
            shape='circle'
          />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RentVsSqftChart;

